import {
    isCancelledError,
    type FetchQueryOptions,
    type Query,
    type QueryClient,
    type QueryKey,
} from "@tanstack/react-query";

import { conversationDetailQueryOptions, updateConversation } from "@/api";

import { invalidateConversationLists } from "./invalidateConversationLists";

export const holdsData = (query: Query) => query.state.data !== undefined;

/** Cancel the query's running read only when it refetches cached data, so a first load keeps running. */
export function cancelQueryRefetch(
    queryClient: QueryClient,
    queryKey: QueryKey
): Promise<void> {
    return queryClient.cancelQueries({
        queryKey,
        exact: true,
        predicate: holdsData,
    });
}

/** Fetch the query and, when a newer fetch cancels this read, resolve with that replacement instead. */
export async function fetchQueryFollowingReplacement<
    TData,
    TKey extends QueryKey,
>(
    queryClient: QueryClient,
    options: FetchQueryOptions<TData, Error, TData, TKey>
): Promise<TData> {
    const currentQuery = () =>
        queryClient.getQueryCache().find<TData>({
            queryKey: options.queryKey,
            exact: true,
        });
    let read = queryClient.fetchQuery(options);
    let source = currentQuery()?.promise;
    for (;;) {
        try {
            return await read;
        } catch (error) {
            const replacement = currentQuery()?.promise;
            if (
                !isCancelledError(error) ||
                !replacement ||
                replacement === source
            ) {
                throw error;
            }
            read = replacement;
            source = replacement;
        }
    }
}

/** Cancel the query's read, apply `update`, mark it stale and restart a cold first read; call it after a write. */
export async function refreshQueryAfterWrite<TData, TKey extends QueryKey>(
    queryClient: QueryClient,
    options: FetchQueryOptions<TData, Error, TData, TKey>,
    update?: (data: TData | undefined) => TData | undefined
): Promise<void> {
    const filters = { queryKey: options.queryKey, exact: true };
    const state = queryClient.getQueryState(options.queryKey);
    const restartColdRead =
        state?.data === undefined && state?.fetchStatus === "fetching";
    const cancellation = queryClient.cancelQueries(filters);
    if (update) {
        queryClient.setQueryData<TData>(options.queryKey, update);
    }
    const invalidation = queryClient.invalidateQueries(filters);
    const restart = restartColdRead
        ? queryClient.fetchQuery({ ...options, staleTime: 0 }).catch(() => {})
        : undefined;
    await Promise.all([cancellation, invalidation, restart]);
}

export async function saveConversationClient(
    queryClient: QueryClient,
    id: string,
    clientId: number | null
): Promise<void> {
    await updateConversation(id, { client_id: clientId });
    await invalidateConversationLists(queryClient);
    await refreshQueryAfterWrite(
        queryClient,
        conversationDetailQueryOptions(id)
    );
}
